import { Platform } from "react-native";
import { secureStorage } from "./secure-storage";

const isWeb = Platform.OS === "web";

export type StoredAccount = Readonly<{
  userId: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  role?: string;
  organization?: string;
}>;

const ACCOUNTS_KEY = "accounts_list";
const ACTIVE_USER_KEY = "active_user_id";

export const accountStore = {
  async getAll(): Promise<StoredAccount[]> {
    const storedAccounts = await secureStorage.getItem(ACCOUNTS_KEY);
    return storedAccounts ? JSON.parse(storedAccounts) : [];
  },

  async getActive(): Promise<StoredAccount | null> {
    const [accounts, activeId] = await Promise.all([
      this.getAll(),
      secureStorage.getItem(ACTIVE_USER_KEY),
    ]);
    return (
      accounts.find((storedAccount) => storedAccount.userId === activeId) ??
      null
    );
  },

  async upsert(account: StoredAccount) {
    const safeAccount = isWeb
      ? { ...account, accessToken: "", refreshToken: "" }
      : account;
    const accounts = await this.getAll();
    const accountIndex = accounts.findIndex(
      (storedAccount) => storedAccount.userId === safeAccount.userId,
    );
    if (accountIndex >= 0) accounts[accountIndex] = safeAccount;
    else accounts.push(safeAccount);
    await secureStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
  },

  async setActive(userId: string) {
    await secureStorage.setItem(ACTIVE_USER_KEY, userId);
  },

  async remove(userId: string) {
    const remainingAccounts = (await this.getAll()).filter(
      (storedAccount) => storedAccount.userId !== userId,
    );
    await secureStorage.setItem(ACCOUNTS_KEY, JSON.stringify(remainingAccounts));
  },

  async clearAll() {
    await secureStorage.multiRemove([ACCOUNTS_KEY, ACTIVE_USER_KEY]);
  },
};
